import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAppBranding } from '../../config/branding';
import config from '../../config/config';
import { useBranding } from '../../context/BrandingContext';
import MyButton from '../../shared-components/button/button';
import Footer from '../../shared-components/Footer/Footer';
import LanguageSelector from '../../shared-components/LanguageSelector/LanguageSelector';
import ToastMessage from '../../shared-components/toast-message/ToastMessage';
import { getLocalStorageItem, setLocalStorageItem } from '../../shared-functions/LocalStorage';
import chargerAPI, { AdhocSessionPayload } from '../../utils/api/charger';
import { normalizeAdhocParameters } from '../../utils/brandingUtils';

/**
 * Generates and stores a new adhoc session token in localStorage
 * @param payload Optional session payload (locationId, evseUid, connectorId)
 */
const storeNewAdhocToken = async (payload?: AdhocSessionPayload) => {
  try {
    const sessionResponse = await chargerAPI.createAdhocSession(payload);
    const sessionData = sessionResponse?.data?.data || sessionResponse?.data;
    if (sessionData?.token) {
      setLocalStorageItem('token', sessionData.token);
      setLocalStorageItem('adhocToken', sessionData.token);
      return sessionData.token;
    }
  } catch (error) {
    console.error('Error generating new adhoc session token:', error);
  }
  return null;
};

/**
 * Component to redirect with device id
 * @returns {React.JSX.Element} React component element
 */
export default function index() {
  const { t } = useTranslation('redirect');
  const { brandData } = useBranding();
  const [searchParameters] = useSearchParams();
  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  let rawDomain = queryParameters.get('domain');

  if (!rawDomain && location.search.includes('domain=')) {
    const match = location.search.match(/domain=([^&?#]*)/);
    if (match) {
      [, rawDomain] = match;
    }
  }

  const {
    deviceId,
    domain: domainParameter,
    connectorId,
  } = normalizeAdhocParameters({
    deviceId: searchParameters.get('deviceId'),
    domain: rawDomain,
    connectorId: searchParameters.get('connectorId'),
  });

  const sessionPayload: AdhocSessionPayload = {
    ...(deviceId && { evseUid: deviceId }),
    ...(connectorId && { connectorId }),
  };

  const navigate = useNavigate();
  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });
  const [appVersion, setAppVersion] = useState<string>('');
  const { images: brandImage } = useAppBranding();

  const cachedLocal = JSON.parse(
    localStorage.getItem(`dynamicBranding_${domainParameter}`) || ('{}' as any),
  );
  const [isTariffAvailable, setIsTariffAvailable] = useState(false);
  const [isTariffLoading, setIsTariffLoading] = useState(true);

  useEffect(() => {
    if (domainParameter) {
      setLocalStorageItem('domain', domainParameter);
    }
  }, [domainParameter]);

  useEffect(() => {
    if (connectorId) {
      setLocalStorageItem('selectedConnector', connectorId);
    }
  }, [connectorId]);

  /**
   * Function routing to pay path
   */
  const handleRoute = () => {
    if (!isTariffAvailable) {
      setToastStatus({
        openToast: true,
        showToastMessage: t('toastTryAnotherDevice'),
      });
      return;
    }

    if (deviceId) {
      const routeParameters = new URLSearchParams();
      if (domainParameter) routeParameters.set('domain', domainParameter);
      if (connectorId) routeParameters.set('connectorId', connectorId);
      const queryString = routeParameters.toString() ? `?${routeParameters.toString()}` : '';
      navigate(`/pay/${deviceId}${queryString}`);
    } else {
      setToastStatus({
        openToast: true,
        showToastMessage: t('toastDeviceNotFound'),
      });
    }
  };

  /**
   * handleVersion fetches the current app version from version.txt
   */
  const handleVersion = async () => {
    try {
      // ✅ Automatically pick current domain
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/version.txt`;

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
      });
      if (!response.ok) {
        setAppVersion('');
        return;
      }

      const contentType = response.headers.get('content-type');

      // ✅ Ensure it's plain text, not HTML
      if (!contentType || !contentType.includes('text/plain')) {
        setAppVersion('');
        return;
      }

      const text = await response.text();

      // ❗ Avoid HTML error response
      if (!text || text.includes('<!DOCTYPE html>')) {
        setAppVersion('');
        return;
      }

      setLocalStorageItem('appVersion', text.trim());
      setAppVersion(text.trim());
    } catch (error) {
      console.error('Version fetch failed:', error);
      setAppVersion('');
    }
  };

  /**
   * Function to check tariff availability
   * @param isRetry Whether this is a retry attempt after token error
   * @returns Promise<void>
   */
  const checkTariffAvailability = async (isRetry = false): Promise<void> => {
    if (!deviceId) {
      setIsTariffAvailable(false);
      setIsTariffLoading(false);
      return;
    }

    try {
      setIsTariffLoading(true);

      const existingToken = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
      if (!existingToken) {
        await storeNewAdhocToken(sessionPayload);
      }

      const response = await chargerAPI.fetchTariffByEvse(deviceId);
      const responseData = response?.data?.data || response?.data;
      const tariffs = Array.isArray(responseData) ? responseData : responseData?.tariffs;

      const hasTariffs = Array.isArray(tariffs) && tariffs.length > 0;

      if (response?.status === 200 && hasTariffs) {
        setIsTariffAvailable(true);
      } else {
        setIsTariffAvailable(false);
        setToastStatus({
          openToast: true,
          showToastMessage: t('toastTryAnotherDevice'),
        });
      }
    } catch (error: any) {
      const isTokenError =
        error?.response?.status === 401 ||
        error?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
        error?.response?.data?.message?.includes('Adhoc token');

      if (isTokenError && !isRetry) {
        const newToken = await storeNewAdhocToken(sessionPayload);
        if (newToken) {
          await checkTariffAvailability(true);
          return;
        }
      }

      console.error('Error fetching tariff:', error);
      setIsTariffAvailable(false);

      setToastStatus({
        openToast: true,
        showToastMessage: error?.response?.data?.message || t('toastSomethingWrong'),
      });
    } finally {
      if (!isRetry) {
        setIsTariffLoading(false);
      }
    }
  };

  useEffect(() => {
    handleVersion();
  }, []);

  useEffect(() => {
    if (deviceId) {
      const baseDeviceId = deviceId.split('_')[0];
      const activeConnector =
        connectorId || (deviceId.includes('_') ? deviceId.split('_')[1] : '1');
      window.location.href = `${config.deepLink}/${baseDeviceId}?connectorId=${activeConnector}`;
    }
  }, []);

  console.log('cachedLocal', cachedLocal);

  useEffect(() => {
    checkTariffAvailability();
  }, [deviceId]);

  return (
    <div className="component-loader-wrap">
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || t('toastSomethingWrong')}
        onCloseTimer={6000}
      />

      <div className="charging-instruction-wrap">
        <div className="navbar-container">
          <img
            src={brandImage.logo}
            alt=""
            className={`navbar-logo ${brandData?.clientName === 'saqr-tech' ? 'saqr-tech' : ''}`}
          />
          <LanguageSelector />
        </div>
        <div className="charging-instruction">
          <h3 className="steps">{t('step1')}</h3>
          <p className="detail">{t('step1Desc')}</p>
          <h3 className="steps">{t('step2')}</h3>
          <p className="detail">{t('step2Desc')}</p>
          <h3 className="steps">{t('step3')}</h3>
          <p className="detail">{t('step3Desc')}</p>
          <h3 className="steps">{t('step4')}</h3>
          <p className="detail">{t('step4Desc')}</p>
          <h3 className="steps">{t('step5')}</h3>
          <p className="detail">{t('step5Desc')}</p>
          <MyButton
            label={t('startBtn')}
            buttonType="submit"
            onClickFunc={handleRoute}
            disableButton={!isTariffAvailable || isTariffLoading}
          />
          <div className={brandData?.footer ? 'app-version' : 'app-version-only'}>
            {brandData?.footer && <Footer />}
            <span>{appVersion}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
